"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFloatingSurfaceVisibility } from "@/hooks/useFloatingSurface";
import { NpsQuestionSet, NpsResponseRequest, NpsScore, NpsShowResponse } from "@/types/nps";
import { apiLive, apiFetch } from "@/lib/api";
import { getNpsScoreBucket } from "@/utils/nps/normalize";
import NpsFeedbackStep from "./NpsFeedbackStep";
import NpsSurveyPrompt from "./NpsSurveyPrompt";

type SurveyStep = "score" | "feedback";

type NpsWriteResponse = {
  success: boolean;
};

export default function NpsSurveyContainer() {
  const { i18n, t } = useTranslation("common");
  const [questions, setQuestions] = useState<NpsQuestionSet | null>(null);
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<SurveyStep>("score");
  const [selectedScore, setSelectedScore] = useState<NpsScore | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedSentRef = useRef(false);
  const inFlightScoreRef = useRef<NpsScore | null>(null);

  const getPageUrl = useCallback(() => {
    return globalThis.window === undefined ? undefined : globalThis.window.location.href;
  }, []);

  const writeResponse = useCallback(
    async (request: NpsResponseRequest): Promise<boolean> => {
      const response = await apiFetch<NpsWriteResponse>(
        "/api/nps/response",
        "POST",
        {
        body: request,
        silent: true,
      },
      );

      return response?.success === true;
    },
    []
  );

  useEffect(() => {
    let mounted = true;
    let showTimer: ReturnType<typeof setTimeout> | null = null;

    apiLive<NpsShowResponse>(
      `/api/nps/show?locale=${encodeURIComponent(i18n.language)}`,
      undefined,
      undefined,
      undefined,
      true
    ).then((response) => {
      if (!mounted || !response?.shouldShow) {
        return;
      }

      setQuestions(response.questions);
      showTimer = setTimeout(() => {
        if (mounted) {
          setVisible(true);
        }
      }, Math.max(response.showDelaySeconds ?? 0, 0) * 1000);
    });

    return () => {
      mounted = false;
      if (showTimer) {
        clearTimeout(showTimer);
      }
    };
  }, [i18n.language]);

  useEffect(() => {
    if (!visible || startedSentRef.current) {
      return;
    }

    startedSentRef.current = true;
    writeResponse({
      status: "started",
      score: null,
      shownAt: new Date().toISOString(),
      pageUrl: getPageUrl(),
    });
  }, [getPageUrl, visible, writeResponse]);

  const handleClose = useCallback(() => {
    setVisible(false);

    if (selectedScore === null) {
      apiFetch("/api/nps/defer", "POST", { silent: true });
      return;
    }

    writeResponse({
      status: "voted",
      score: selectedScore,
      votedAt: new Date().toISOString(),
      pageUrl: getPageUrl(),
    });
  }, [getPageUrl, selectedScore, writeResponse]);

  const handleSelectScore = useCallback(
    async (score: NpsScore) => {
      if (inFlightScoreRef.current === score) {
        return;
      }

      if (selectedScore === score) {
        setStep("feedback");
        return;
      }

      setSelectedScore(score);
      setStep("feedback");
      setError(null);
      setIsSubmitting(true);
      inFlightScoreRef.current = score;

      const saved = await writeResponse({
        status: "voted",
        score,
        votedAt: new Date().toISOString(),
        pageUrl: getPageUrl(),
      });

      if (!saved) {
        setError(t("nps.saveScoreError"));
      }

      inFlightScoreRef.current = null;
      setIsSubmitting(false);
    },
    [getPageUrl, selectedScore, t, writeResponse]
  );

  const handleSubmitFeedback = useCallback(async () => {
    if (selectedScore === null || !questions) {
      return;
    }

    setError(null);
    setIsSubmitting(true);
    const trimmedFeedback = feedback.trim();
    const bucket = getNpsScoreBucket(selectedScore);
    const saved = await writeResponse({
      status: trimmedFeedback ? "completed" : "voted",
      score: selectedScore,
      feedback: trimmedFeedback || undefined,
      followUpQuestion: questions[bucket],
      completedAt: trimmedFeedback ? new Date().toISOString() : undefined,
      votedAt: trimmedFeedback ? undefined : new Date().toISOString(),
      pageUrl: getPageUrl(),
    });

    setIsSubmitting(false);

    if (saved) {
      setVisible(false);
      return;
    }

    setError(t("nps.saveFeedbackError"));
  }, [feedback, getPageUrl, questions, selectedScore, t, writeResponse]);

  useFloatingSurfaceVisibility("nps", visible && questions !== null);

  if (!visible || !questions) {
    return null;
  }

  return (
    <div className="fixed inset-x-4 bottom-4 z-50 flex justify-center">
      <div className="w-full max-w-xl space-y-2">
        {step === "score" || selectedScore === null ? (
          <NpsSurveyPrompt
            questions={questions}
            selectedScore={selectedScore}
            disabled={isSubmitting}
            onClose={handleClose}
            onSelectScore={handleSelectScore}
          />
        ) : (
          <NpsFeedbackStep
            questions={questions}
            bucket={getNpsScoreBucket(selectedScore)}
            feedback={feedback}
            disabled={isSubmitting}
            onBack={() => setStep("score")}
            onClose={handleClose}
            onFeedbackChange={setFeedback}
            onSubmit={handleSubmitFeedback}
          />
        )}
        {error && (
          <p className="rounded-md bg-panel px-3 py-2 text-sm text-destructive shadow">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
